import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField as MuiTextField,
  useTheme,
  useMediaQuery,
  Autocomplete,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
} from "@mui/material";
import { Download } from "@mui/icons-material";
import Parse from "parse";
import * as XLSX from "xlsx";
import CustomPagination from "../../Components/CustomPagination";
import { Summary } from "./Summary/Summary";

const roleHierarchy = [
  "Super-User",
  "Game-Owner",
  "Master-Distributor",
  "Distributor",
  "Sub-Distributor",
  "Agent",
  "Player",
];

const filterModes = [
  { label: "Role", value: "role" },
  { label: "User", value: "manager" },
];

export const AccountsList = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  // Filter states
  const [roleOptions, setRoleOptions] = useState([]);
  const [selectedRole, setSelectedRole] = useState("All");
  const [filterMode, setFilterMode] = useState("role");

  const today = new Date().toISOString().split("T")[0];
  const oneMonthAgo = new Date();
  oneMonthAgo.setDate(oneMonthAgo.getDate() - 30);
  const defaultStartDate = oneMonthAgo.toISOString().split("T")[0];

  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate] = useState(today);
  const [manager, setManager] = useState(null);
  const [managerOptions, setManagerOptions] = useState([]);
  const [loadingManagers, setLoadingManagers] = useState(false);

  // Data states
  const [data, setData] = useState([]);
  const [summaryData, setSummaryData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  // Export dialog states
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportMonth, setExportMonth] = useState("");
  const [isExporting, setIsExporting] = useState(false);

  // Fetch role options on mount
  useEffect(() => {
    const fetchRole = async () => {
      try {
        const user = await Parse.User.current().fetch();
        const roleQuery = new Parse.Query(Parse.Role);
        roleQuery.equalTo("users", user);
        const currentRole = await roleQuery.first({ useMasterKey: true });

        let options = [{ label: "All", value: "All" }];
        if (currentRole) {
          const idx = roleHierarchy.indexOf(currentRole.get("name"));
          if (idx >= 0) {
            const lowerRoles = roleHierarchy.slice(idx + 1).map((r) => ({
              label: r,
              value: r,
            }));
            options = [{ label: "All", value: "All" }, ...lowerRoles];
          }
        }
        setRoleOptions(options);
      } catch (err) {
        console.error("Error fetching role:", err);
        setRoleOptions(roleHierarchy.map((r) => ({ label: r, value: r })));
      }
    };
    fetchRole();
  }, []);

  // Fetch data from Cloud Function
  const fetchData = async ({ isReset = false } = {}) => {
    setLoading(true);
    try {
      const params = {};
      if (!isReset) {
        params.page = page;
        params.limit = perPage;
        if (startDate) params.startDate = startDate;
        if (endDate) params.endDate = endDate;
      } else {
        params.page = 1;
        params.limit = perPage;
      }

      if (filterMode === "manager" && manager) {
        params.userId = manager.id;
        params.filterExactUser = true;
      } else if (filterMode === "role" && selectedRole !== "All") {
        params.roleName = selectedRole;
      }

      const response = await Parse.Cloud.run("getTotalAccountsReport", params);

      if (response.success) {
        setData(response.data);
        setSummaryData(response.summary);
        setTotal(response.total);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch data on mount and when filters/pagination change
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, perPage]);

  // Manager search
  const handleManagerSearch = async (inputValue) => {
    if (!inputValue) {
      setManagerOptions([]);
      return;
    }
    setLoadingManagers(true);
    try {
      const query = new Parse.Query(Parse.User);
      query.limit(20);
      query.select(["username", "role"]);
      query.include("role");
      query.matches("username", inputValue, "i");

      const results = await query.find();
      const users = results.map((u) => ({
        id: u.id,
        username: u.get("username"),
        role: u.get("role")?.get("name") || "Unknown",
      }));
      setManagerOptions(users);
    } catch (err) {
      console.error("Manager search failed:", err);
    } finally {
      setLoadingManagers(false);
    }
  };

  const handleApply = () => {
    setPage(1); // Reset to first page
    fetchData();
  };

  const handleClear = () => {
    setStartDate("");
    setEndDate("");
    setSelectedRole("All");
    setManager(null);
    setManagerOptions([]);
    setPage(1);
    fetchData({ isReset: true });
  };

  const handleOpenExportDialog = () => {
    // Set default month to current month
    const now = new Date();
    const defaultMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    setExportMonth(defaultMonth);
    setExportDialogOpen(true);
  };

  const handleExportExcel = async () => {
    if (!exportMonth) {
      alert("Please select a month");
      return;
    }

    setIsExporting(true);
    try {
      // Calculate start and end dates from selected month
      const [year, month] = exportMonth.split("-");
      const monthStartDate = `${year}-${month}-01`;
      const lastDay = new Date(parseInt(year), parseInt(month), 0).getDate();
      const monthEndDate = `${year}-${month}-${String(lastDay).padStart(2, "0")}`;

      // Fetch all data in a single request using exportAll parameter
      const params = {
        startDate: monthStartDate,
        endDate: monthEndDate,
        exportAll: true,
        exportMonth: exportMonth,
      };

      if (filterMode === "manager" && manager) {
        params.userId = manager.id;
        params.filterExactUser = true;
      } else if (filterMode === "role" && selectedRole !== "All") {
        params.roleName = selectedRole;
      }

      const response = await Parse.Cloud.run("getTotalAccountsReport", params);

      if (!response.success) {
        alert("Failed to fetch data");
        return;
      }

      if (response.data.length > 0) {
        // Prepare data for Excel
        const excelData = response.data.map((row) => ({
          Username: row.username,
          "Role Name": row.roleName,
          Balance: row.balance,
          "Parent Name": row.managerName,
          "Total Played": row.totalBetAmount,
          "Total Win": row.totalWinAmount,
          "Total Recharge": row.totalRecharge,
          "Total Redeem": row.totalRedeem,
          "Total Income": row.netAmount,
          "Holding (%)": row.rechargeRatio,
        }));

        // Create worksheet
        const ws = XLSX.utils.json_to_sheet(excelData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Accounts Report");

        // Generate filename with selected month
        const filename = `Accounts_Report_${exportMonth}.xlsx`;

        XLSX.writeFile(wb, filename);
        setExportDialogOpen(false);
      } else {
        alert("No data to export for the selected month");
      }
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      alert("Failed to export data");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <React.Fragment>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "calc(100vh - 80px)",
        }}
      >
        {/* Sticky Summary Cards */}
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 2,
            backgroundColor: "#1a1a1a",
            pb: 2,
          }}
        >
          <Summary summaryData={summaryData} />
        </Box>

        {/* Scrollable Content */}
        <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden", pt: 2 }}>
          <Box
            sx={{
              backgroundColor: "#242424",
              borderRadius: 2,
              boxShadow: "none !important",
              padding: isSmallScreen ? { xs: 0, sm: 0 } : { xs: 1.5, sm: 2 },
            }}
          >
            {/* Filters */}
            <Box
              display={{ xs: "block", sm: "flex" }}
              alignItems="center"
              gap={2}
              sx={{ mb: 3 }}
            >
              <MuiTextField
                size="small"
                type="date"
                label="Start Date"
                InputLabelProps={{ shrink: true }}
                value={startDate}
                onChange={(e) => {
                  const newStart = e.target.value;
                  setStartDate(newStart);
                  if (endDate && newStart > endDate) {
                    setEndDate("");
                  }
                }}
                inputProps={{ max: today }}
              />
              <MuiTextField
                size="small"
                type="date"
                label="End Date"
                InputLabelProps={{ shrink: true }}
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                inputProps={{ min: startDate || undefined, max: today }}
              />

              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Filter By</InputLabel>
                <Select
                  value={filterMode}
                  label="Filter By"
                  onChange={(e) => {
                    setFilterMode(e.target.value);
                    setManager(null);
                    setSelectedRole("All");
                  }}
                >
                  {filterModes.map((mode) => (
                    <MenuItem key={mode.value} value={mode.value}>
                      {mode.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {filterMode === "role" && (
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Role</InputLabel>
                  <Select
                    value={selectedRole}
                    label="Role"
                    onChange={(e) => setSelectedRole(e.target.value)}
                  >
                    {roleOptions.map((r) => (
                      <MenuItem key={r.value} value={r.value}>
                        {r.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {filterMode === "manager" && (
                <Autocomplete
                  size="small"
                  sx={{ minWidth: 220 }}
                  options={managerOptions}
                  value={manager}
                  getOptionLabel={(option) =>
                    option ? `${option.username} (${option.role})` : ""
                  }
                  loading={loadingManagers}
                  onInputChange={(e, newValue) => handleManagerSearch(newValue)}
                  onChange={(e, newValue) => setManager(newValue)}
                  renderInput={(params) => (
                    <MuiTextField
                      {...params}
                      label="User"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingManagers ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              )}

              <Box display="flex" width="max-content">
                <Button
                  variant="contained"
                  sx={{
                    mt: 1,
                    mb: 1,
                    mr: 1,
                    background: "#FFC107",
                    color: "#0A0A0A",
                    textTransform: "none",
                    "&:hover": { backgroundColor: "#FFA000" },
                  }}
                  onClick={handleApply}
                >
                  Apply
                </Button>
                <Button
                  variant="outlined"
                  sx={{
                    mt: 1,
                    mb: 1,
                    mr: 1,
                    outline: "1px solid #FFC107",
                    color: "white",
                    textTransform: "none",
                    border: "none",
                  }}
                  onClick={handleClear}
                >
                  Clear
                </Button>
                <Button
                  variant="contained"
                  startIcon={<Download />}
                  disabled={loading || data.length === 0}
                  sx={{
                    mt: 1,
                    mb: 1,
                    background: "#4CAF50",
                    color: "white",
                    textTransform: "none",
                    "&:hover": { backgroundColor: "#45a049" },
                    "&:disabled": { backgroundColor: "#666", color: "#999" },
                  }}
                  onClick={handleOpenExportDialog}
                >
                  Export Excel
                </Button>
              </Box>
            </Box>

            {/* Table */}
            {loading ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="400px"
              >
                <CircularProgress />
              </Box>
            ) : (
              <>
                <TableContainer
                  component={Paper}
                  sx={{ backgroundColor: "#2a2a2a" }}
                >
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ color: "#FFC107", fontWeight: 700 }}>
                          Username
                        </TableCell>
                        <TableCell sx={{ color: "#FFC107", fontWeight: 700 }}>
                          Role Name
                        </TableCell>
                        <TableCell sx={{ color: "#FFC107", fontWeight: 700 }}>
                          Balance
                        </TableCell>
                        <TableCell sx={{ color: "#FFC107", fontWeight: 700 }}>
                          Parent Name
                        </TableCell>
                        <TableCell sx={{ color: "#FFC107", fontWeight: 700 }}>
                          Total Played
                        </TableCell>
                        <TableCell sx={{ color: "#FFC107", fontWeight: 700 }}>
                          Total Win
                        </TableCell>
                        <TableCell sx={{ color: "#FFC107", fontWeight: 700 }}>
                          Total Recharge
                        </TableCell>
                        <TableCell sx={{ color: "#FFC107", fontWeight: 700 }}>
                          Total Redeem
                        </TableCell>
                        <TableCell sx={{ color: "#FFC107", fontWeight: 700 }}>
                          Total Income
                        </TableCell>
                        <TableCell sx={{ color: "#FFC107", fontWeight: 700 }}>
                          Holding (%)
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.map((row) => (
                        <TableRow key={row.id}>
                          <TableCell sx={{ color: "white" }}>
                            {row.username}
                          </TableCell>
                          <TableCell sx={{ color: "white" }}>
                            {row.roleName}
                          </TableCell>
                          <TableCell sx={{ color: "white" }}>
                            {row.balance}
                          </TableCell>
                          <TableCell sx={{ color: "white" }}>
                            {row.managerName}
                          </TableCell>
                          <TableCell sx={{ color: "white" }}>
                            {row.totalBetAmount}
                          </TableCell>
                          <TableCell sx={{ color: "white" }}>
                            {row.totalWinAmount}
                          </TableCell>
                          <TableCell sx={{ color: "white" }}>
                            {row.totalRecharge}
                          </TableCell>
                          <TableCell sx={{ color: "white" }}>
                            {row.totalRedeem}
                          </TableCell>
                          <TableCell sx={{ color: "white" }}>
                            {row.netAmount}
                          </TableCell>
                          <TableCell sx={{ color: "white" }}>
                            {row.rechargeRatio}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    width: "100% !important",
                    margin: "16px 0px",
                  }}
                >
                  <CustomPagination
                    page={page}
                    perPage={perPage}
                    total={total}
                    setPage={setPage}
                    setPerPage={setPerPage}
                  />
                </Box>
              </>
            )}
          </Box>
        </Box>
      </Box>

      {/* Export Month Dialog */}
      <Dialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Select Month to Export</DialogTitle>
        <DialogContent>
          <MuiTextField
            label="Month"
            type="month"
            value={exportMonth}
            onChange={(e) => setExportMonth(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            sx={{ mt: 1 }}
          />
          {isExporting && (
            <Box sx={{ display: "flex", alignItems: "center", mt: 2 }}>
              <CircularProgress size={24} sx={{ mr: 2 }} />
              <Typography variant="body2">Exporting...</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: 2 }} className="custom-modal-footer">
          <Box
            className="d-flex w-100 justify-content-between"
            sx={{
              flexDirection: { xs: "column-reverse", sm: "row" },
              alignItems: { xs: "stretch", sm: "stretch" },
              gap: { xs: 2, sm: 2 },
              marginBottom: { xs: 2, sm: 2 },
              width: "100% !important",
              paddingRight: { xs: 0, sm: 1 },
            }}
          >
            <Button
              onClick={() => setExportDialogOpen(false)}
              disabled={isExporting}
              className="custom-button cancel"
            >
              Cancel
            </Button>
            <Button
              className="custom-button confirm"
              onClick={handleExportExcel}
              disabled={!exportMonth || isExporting}
            >
              Export Excel
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
};
